// Bloc « Partenaires » de la fiche (conception du 29/10/2026, § 7) : à qui mon organisation a partagé la fiche,
// partager à un partenaire (avec l'affaire au choix), retirer un partage, inviter un nouveau partenaire. Jamais pour
// un invité : il ne partage pas.
import { UserPlus, X } from "lucide-react";
import { useGetList, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Project, ProjectShare, Partnership, Sale } from "../types";
import { useProjectDeal } from "./DealFollowUp";
import { HelpTip } from "./HelpTip";
import { InvitePartnerDialog } from "./InvitePartnerDialog";
import {
  canHostPartners,
  canUnshare,
  shareCandidates,
  transmitByDefault,
} from "./partnerships";
import { usePartnerOrg } from "./usePartnerOrg";

export const ProjectShares = ({ project }: { project: Project }) => {
  const { sale } = usePartnerOrg();
  if (!canHostPartners(sale) || sale?.org_id == null) return null;
  return (
    <ProjectSharesCard
      project={project}
      me={sale.id}
      myOrgId={sale.org_id}
      orgAdmin={!!sale.org_admin}
    />
  );
};

const ProjectSharesCard = ({
  project,
  me,
  myOrgId,
  orgAdmin,
}: {
  project: Project;
  me: Sale["id"];
  myOrgId: number;
  orgAdmin: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [choice, setChoice] = useState("");
  // null : pas encore touchée, la valeur par défaut dépend de qui suit l'affaire.
  const [withDeal, setWithDeal] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviting, setInviting] = useState(false);
  const { deal, refetch: refetchDeal } = useProjectDeal(project);
  const { data: partnerships, refetch: refetchPartnerships } =
    useGetList<Partnership>("partnerships", {
      filter: { host_org_id: myOrgId, "ended_at@is": null },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
    });
  const { data: shares, refetch: refetchShares } = useGetList<ProjectShare>(
    "project_shares",
    {
      filter: {
        project_id: project.id,
        host_org_id: myOrgId,
        "revoked_at@is": null,
      },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "created_at", order: "ASC" },
    },
  );
  // Comptes liés (partenaires et auteurs des partages) : leur lecture est permise à l'organisation.
  const ids = [
    ...new Set([
      ...(partnerships ?? []).map((p) => p.partner_sale_id),
      ...(shares ?? []).flatMap((s) => [s.partner_sale_id, s.shared_by]),
      deal?.sales_id == null ? null : Number(deal.sales_id),
    ]),
  ].filter((id): id is number => id != null);
  const { data: sales, refetch: refetchSales } = useGetList<Sale>(
    "sales",
    {
      filter: { "id@in": `(${ids.join(",")})` },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: ids.length > 0 },
  );
  if (!partnerships || !shares) return null;

  const sale = (id: number | null) => sales?.find((s) => Number(s.id) === id);
  const name = (id: number) => {
    const s = sale(id);
    return s ? `${s.first_name} ${s.last_name}`.trim() : `#${id}`;
  };
  const candidates = shareCandidates(partnerships, shares);
  const transmit = withDeal ?? transmitByDefault(deal, me);
  // Quelqu'un d'autre que moi suit l'affaire : la transmettre la lui reprend, on le dit.
  const holder =
    deal?.sales_id != null && Number(deal.sales_id) !== Number(me)
      ? Number(deal.sales_id)
      : null;

  const refresh = () => {
    refetchShares();
    refetchPartnerships();
    refetchSales();
    refetchDeal();
  };

  // La base rend le partage existant sans erreur si la fiche est déjà partagée à ce partenaire.
  const share = async (
    partner: number,
    transmit: boolean,
    label: string,
    message?: string,
    quiet = false,
  ) => {
    const { error } = await getSupabaseClient().rpc("share_project", {
      project: project.id,
      partner,
      transmit,
    });
    refresh();
    if (error) {
      notify(error.message, { type: "error" });
      return false;
    }
    if (quiet) return true;
    notify(
      message ??
        (transmit
          ? "resources.projects.partners.shared_deal"
          : "resources.projects.partners.shared"),
      { type: "info", messageArgs: { name: label } },
    );
    return true;
  };

  const submit = async () => {
    setBusy(true);
    const partner = Number(choice);
    const done = await share(partner, transmit, name(partner));
    setBusy(false);
    if (done) {
      setChoice("");
      setWithDeal(null);
    }
  };

  // Fiche déjà partagée : transmettre l'affaire seule (même fonction, le partage existant est gardé).
  const transmitTo = async (partner: number) => {
    setBusy(true);
    await share(
      partner,
      true,
      name(partner),
      "resources.projects.partners.deal_transmitted",
    );
    setBusy(false);
  };

  const unshare = async (shareId: number) => {
    setBusy(true);
    const { error } = await getSupabaseClient().rpc("unshare_project", {
      share: shareId,
    });
    setBusy(false);
    refresh();
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify("resources.projects.partners.unshared", { type: "info" });
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 text-sm">
        <h3 className="flex items-center gap-1 font-bold">
          {translate("resources.projects.partners.block_title")}
          <HelpTip topic="partners" />
        </h3>
        {shares.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground tracking-wide">
              {translate("resources.projects.partners.shared_with")}
            </span>
            <div className="flex flex-wrap gap-2">
              {shares.map((item) => (
                <Badge
                  key={item.id}
                  variant="outline"
                  className="h-auto max-w-full gap-1 overflow-visible whitespace-normal"
                >
                  {name(item.partner_sale_id)}
                  {item.shared_by != null && (
                    <span className="text-muted-foreground">
                      ·{" "}
                      {translate("resources.projects.partners.shared_by", {
                        name: sale(item.shared_by)?.first_name ?? "",
                      })}
                    </span>
                  )}
                  {deal && Number(deal.sales_id) === item.partner_sale_id ? (
                    <span className="text-muted-foreground">
                      · {translate("resources.projects.partners.deal_holder")}
                    </span>
                  ) : (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <button
                        type="button"
                        title={
                          holder != null
                            ? translate(
                                "resources.projects.partners.transmit_deal_held",
                                { name: name(holder) },
                              )
                            : undefined
                        }
                        // Cible élargie au doigt sans grossir la puce.
                        className="relative underline underline-offset-2 after:absolute after:-inset-y-3.5 after:-inset-x-1 after:content-['']"
                        disabled={busy}
                        onClick={() => transmitTo(item.partner_sale_id)}
                      >
                        {translate("resources.projects.partners.transmit_deal")}
                      </button>
                    </>
                  )}
                  {canUnshare(item, me, orgAdmin) && (
                    <button
                      type="button"
                      aria-label={translate(
                        "resources.projects.partners.unshare",
                      )}
                      title={translate("resources.projects.partners.unshare")}
                      // Cible élargie au doigt sans grossir la puce.
                      className="relative inline-flex after:absolute after:-inset-3.5 after:content-['']"
                      disabled={busy}
                      onClick={() => unshare(item.id)}
                    >
                      <X className="size-3.5" aria-hidden />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {/* Sans candidat, la liste disparaît : on dit pourquoi, sinon le bloc semble bloqué. */}
        {candidates.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {translate(
              partnerships.length
                ? "resources.projects.partners.all_shared"
                : "resources.projects.partners.no_partner",
            )}
          </p>
        )}
        {candidates.length > 0 && (
          <>
            <div className="flex gap-2">
              <Select value={choice} onValueChange={setChoice}>
                <SelectTrigger className="flex-1 min-w-0 data-[size=default]:h-11 md:data-[size=default]:h-9">
                  <SelectValue
                    placeholder={translate(
                      "resources.projects.partners.choose",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map((id) => (
                    <SelectItem key={id} value={String(id)}>
                      {name(id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                className="min-h-11 md:min-h-8"
                disabled={!choice || busy}
                onClick={submit}
              >
                {translate("resources.projects.partners.share")}
              </Button>
            </div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <Checkbox
                checked={transmit}
                onCheckedChange={(checked) => setWithDeal(checked === true)}
              />
              <span>
                {holder != null
                  ? translate("resources.projects.partners.with_deal_held", {
                      name: name(holder),
                    })
                  : translate("resources.projects.partners.with_deal")}
              </span>
            </label>
          </>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="self-start -ml-2 min-h-11 md:min-h-8"
          onClick={() => setInviting(true)}
        >
          <UserPlus className="size-4" aria-hidden />
          {translate("resources.projects.partners.invite.action")}
        </Button>
        <InvitePartnerDialog
          fromProject
          transmitDefault={transmitByDefault(deal, me)}
          dealHolder={holder != null ? name(holder) : undefined}
          open={inviting}
          onOpenChange={setInviting}
          onInvited={({ partnerSaleId, email }, transmitDeal) =>
            // Le dialogue dit en une fois l'invitation et le partage.
            share(partnerSaleId, transmitDeal, email, undefined, true)
          }
        />
      </CardContent>
    </Card>
  );
};
